import { toSamples } from './series';
import type { Sample, SystemStatsResponse } from './types';

/**
 * Pull the message out of a failed response.
 *
 * The server sends `{"error": "..."}` on failure - see
 * `build_json_error_response` - but a 500 from a layer above the handler can
 * still arrive as HTML, so fall back to the status line.
 */
async function errorMessage(response: Response): Promise<string> {
  let message = `${response.status} ${response.statusText}`;

  try {
    const body = await response.json();
    if (body?.error) message = body.error;
  } catch {
    // Response body wasn't JSON - use status text
  }

  return message;
}

/**
 * One server's system stats, oldest first.
 *
 * An empty list is a normal answer, not an error: the processor's collection
 * loop primes its counters before recording anything, so a freshly restarted
 * server genuinely has no snapshots for the first ten seconds.
 */
export async function fetchSystemStats(serverId: number): Promise<Sample[]> {
  const response = await fetch(`/admin/api/servers/${serverId}/system-stats`);

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  const body = await response.json();

  // Validate at the boundary. The endpoint returns a bare map with no envelope,
  // and an array also passes `typeof body === 'object'` - a stale server build
  // answering with a list would otherwise parse into a silently empty chart
  // rather than an error.
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    throw new Error(`Unexpected system stats response: ${JSON.stringify(body)}`);
  }

  return toSamples(body as SystemStatsResponse);
}
